/**
 * B1055 — Journal d'incidents (Eric 2026-09-04 SPEC STRICK PROD).
 *
 * POURQUOI CETTE PAGE EXISTE
 * ──────────────────────────
 * Pendant l'incident des 2 et 3 septembre 2026, dix correctifs successifs
 * (B1041 → B1050) ont chacun traité un symptôme différent — faute de trace
 * écrite de ce qui avait déjà été éliminé. Deux jours de production perdus.
 *
 * Cette page garde donc, pour chaque panne : ce que le système a détecté, la
 * **cause réelle** et le **correctif**, tous deux éditables. C'est ce qui rend
 * un incident compréhensible six mois plus tard, quand le contexte est oublié.
 * Le journal est pré-rempli avec l'historique documenté de B1040 à B1053.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Warning, PencilSimple, Plus, FloppyDisk, X, MagnifyingGlass, Brain } from "@phosphor-icons/react";
import { useLang } from "../../contexts/LanguageContext";

const API = process.env.REACT_APP_BACKEND_URL;

const SEVERITY = {
  critical: { label: "Critique", cls: "bg-red-100 text-red-800 border-red-300" },
  warning: { label: "Avertissement", cls: "bg-amber-100 text-amber-900 border-amber-300" },
  info: { label: "Information", cls: "bg-sky-100 text-sky-900 border-sky-300" },
};

const STATUS = {
  open: { label: "Ouvert", label_en: "Open", cls: "bg-red-600 text-white" },
  acknowledged: { label: "Pris en compte", label_en: "Acknowledged", cls: "bg-amber-500 text-white" },
  resolved: { label: "Résolu", label_en: "Resolved", cls: "bg-emerald-600 text-white" },
};

const FILTERS = [
  { key: "", label: "Tous", label_en: "All" },
  { key: "open", label: "Ouverts", label_en: "Open" },
  { key: "acknowledged", label: "Pris en compte", label_en: "Acknowledged" },
  { key: "resolved", label: "Résolus", label_en: "Resolved" },
];

/**
 * B1059 — Surligne les mots recherchés. Sans repère visuel, sur une cause
 * racine de dix lignes, Eric doit relire tout le paragraphe pour comprendre
 * pourquoi l'incident est ressorti.
 */
function Highlighted({ text, terms }) {
  if (!text) return null;
  if (!terms || terms.length === 0) return <>{text}</>;
  const pattern = new RegExp(`(${terms.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")})`, "gi");
  // On compare sans accents pour que « requete » surligne « requête ».
  const strip = (v) => v.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  return (
    <>
      {text.split(/(\s+)/).map((word, i) => {
        const bare = strip(word);
        pattern.lastIndex = 0;
        return pattern.test(bare) ? (
          <mark key={i} className="bg-yellow-200 rounded px-0.5">{word}</mark>
        ) : (
          <span key={i}>{word}</span>
        );
      })}
    </>
  );
}

/**
 * Tâche 1243 (Eric 2026-06-28) — Regrouper les alertes qui reviennent en boucle.
 *
 * Une même condition qui apparaît et disparaît remplissait le journal : 405
 * lignes pour la seule clé `byok_keys`, 537 des 555 entrées en doublons. Les
 * VRAIES pannes, documentées à la main, étaient noyées. On affiche donc une
 * ligne par FAMILLE (même identifiant technique), avec le nombre de retours et
 * un dépliant pour voir le détail.
 */
export function groupFamilies(incidents) {
  const byKey = new Map();
  (incidents || []).forEach((inc) => {
    const k = inc.key || inc.id;
    if (!byKey.has(k)) byKey.set(k, []);
    byKey.get(k).push(inc);
  });
  return [...byKey.entries()].map(([key, entries]) => {
    // La ligne montrée dit la vérité du jour : si la panne est encore
    // ouverte, c'est celle-là qu'il faut lire en premier.
    const head = entries.find((e) => e.status === "open") || entries[0];
    const returns =
      entries.reduce((n, e) => n + (e.reopen_count || 0), 0) + entries.length - 1;
    const occurrences = entries.reduce((n, e) => n + (e.occurrences || 1), 0);
    const lastSeen = entries
      .map((e) => e.last_seen_at || e.resolved_at || e.detected_at)
      .filter(Boolean)
      .sort()
      .slice(-1)[0];
    return {
      key,
      head,
      entries,
      others: entries.filter((e) => e !== head),
      returns,
      occurrences,
      lastSeen,
    };
  });
}

const fmt = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("fr-CA", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return String(iso);
  }
};

function IncidentEditor({ incident, onSave, onCancel }) {
  // Leçon 1373 — un sous-composant n'hérite pas du `tr` de sa page.
  const { tr } = useLang();
  const [rootCause, setRootCause] = useState(incident.root_cause || "");
  const [fix, setFix] = useState(incident.fix || "");
  const [status, setStatus] = useState(incident.status || "open");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    await onSave({ root_cause: rootCause, fix, status });
    setSaving(false);
  };

  return (
    <div className="mt-3 space-y-3 border-t border-zinc-200 pt-3" data-testid={`incident-editor-${incident.id}`}>
      <div>
        <label className="block text-xs font-semibold uppercase tracking-wide text-zinc-500 mb-1">
          {tr("Cause réelle", "Real cause")}
        </label>
        <textarea
          value={rootCause}
          onChange={(e) => setRootCause(e.target.value)}
          rows={3}
          placeholder={tr("Qu'est-ce qui a VRAIMENT provoqué la panne ? Pas le symptôme.", "What ACTUALLY caused the outage? Not the symptom.")}
          className="w-full text-sm rounded-lg border border-zinc-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#002FA7]/30"
          data-testid={`incident-root-cause-input-${incident.id}`}
        />
      </div>
      <div>
        <label className="block text-xs font-semibold uppercase tracking-wide text-zinc-500 mb-1">
          {tr("Correctif appliqué", "Fix applied")}
        </label>
        <textarea
          value={fix}
          onChange={(e) => setFix(e.target.value)}
          rows={3}
          placeholder={tr("Qu'a-t-on changé, et pourquoi ça empêche la panne de revenir ?", "What did we change, and why does it stop the outage coming back?")}
          className="w-full text-sm rounded-lg border border-zinc-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#002FA7]/30"
          data-testid={`incident-fix-input-${incident.id}`}
        />
      </div>
      <div className="flex items-center gap-3 flex-wrap">
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="text-sm rounded-lg border border-zinc-300 px-3 py-2"
          data-testid={`incident-status-select-${incident.id}`}
        >
          <option value="open">{tr("Ouvert", "Open")}</option>
          <option value="acknowledged">{tr("Pris en compte", "Acknowledged")}</option>
          <option value="resolved">{tr("Résolu", "Resolved")}</option>
        </select>
        <button
          onClick={save}
          disabled={saving}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#002FA7] hover:bg-[#002280] text-white text-sm font-semibold disabled:opacity-50"
          data-testid={`incident-save-btn-${incident.id}`}
        >
          <FloppyDisk size={16} />
          {saving ? "Enregistrement…" : "Enregistrer"}
        </button>
        <button
          onClick={onCancel}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-zinc-300 text-sm text-zinc-700 hover:bg-zinc-50"
          data-testid={`incident-cancel-btn-${incident.id}`}
        >
          <X size={14} />
          {tr("Annuler", "Cancel")}
        </button>
      </div>
    </div>
  );
}

export default function IncidentJournalPage() {
  const { tr, lang } = useLang();
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("");
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [query, setQuery] = useState("");
  const [semantic, setSemantic] = useState(false);
  const [openFamily, setOpenFamily] = useState(null);   // Tâche 1243
  const [searching, setSearching] = useState(false);
  const [draft, setDraft] = useState({ title: "", message: "", severity: "warning", root_cause: "", fix: "" });

  const load = useCallback(async () => {
    setSearching(true);
    try {
      const params = new URLSearchParams({ limit: "200" });
      if (filter) params.set("status", filter);
      if (query.trim()) {
        params.set("q", query.trim());
        if (semantic) params.set("semantic", "true");
      }
      const { data: res } = await axios.get(`${API}/api/incidents?${params.toString()}`, {
        withCredentials: true,
      });
      setData(res);
    } catch (e) {
      toast.error(tr("Erreur : ", "Error: ") + (e?.response?.data?.detail || e.message));
    } finally {
      setSearching(false);
    }
  }, [filter, query, semantic]);

  // Anti-rafale : la recherche par sens appelle le modèle d'embeddings, on
  // évite d'en déclencher une à chaque frappe.
  useEffect(() => {
    const t = setTimeout(load, query.trim() ? 400 : 0);
    return () => clearTimeout(t);
  }, [load, query]);

  const saveIncident = async (id, patch) => {
    try {
      await axios.patch(`${API}/api/incidents/${id}`, patch, { withCredentials: true });
      toast.success(tr("Incident mis à jour", "Incident updated"));
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(tr("Erreur : ", "Error: ") + (e?.response?.data?.detail || e.message));
    }
  };

  const createIncident = async () => {
    if (draft.title.trim().length < 3) {
      toast.error(tr("Le titre doit contenir au moins 3 caractères", "The title needs at least 3 characters"));
      return;
    }
    try {
      await axios.post(`${API}/api/incidents`, draft, { withCredentials: true });
      toast.success(tr("Incident ajouté au journal", "Incident added to the journal"));
      setCreating(false);
      setDraft({ title: "", message: "", severity: "warning", root_cause: "", fix: "" });
      await load();
    } catch (e) {
      toast.error(tr("Erreur : ", "Error: ") + (e?.response?.data?.detail || e.message));
    }
  };

  const counts = data?.counts || {};
  const incidents = useMemo(() => data?.incidents || [], [data]);
  const families = useMemo(() => groupFamilies(incidents), [incidents]);

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6" data-testid="incident-journal-page">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-zinc-900 flex items-center gap-2">
            <Warning size={26} weight="fill" className="text-[#002FA7]" />
            {tr("Journal d'incidents", "Incident journal")}
          </h1>
          <p className="text-sm text-zinc-600 mt-1 max-w-2xl">
            {tr(
              "Chaque panne détectée, avec sa cause réelle et son correctif. En septembre 2026, dix correctifs successifs ont traité des symptômes faute de trace écrite de ce qui avait déjà été éliminé — deux jours de production perdus. Ce journal existe pour que ça ne se reproduise pas.",
              "Every outage we caught, with its real cause and its fix. In September 2026 ten fixes in a row treated symptoms, because nothing written down said what had already been ruled out — two days of production lost. This journal exists so that does not happen again.",
            )}
          </p>
        </div>
        <button
          onClick={() => setCreating((v) => !v)}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#002FA7] hover:bg-[#002280] text-white text-sm font-semibold"
          data-testid="incident-new-btn"
        >
          <Plus size={16} weight="bold" />
          {tr("Ajouter une panne", "Add an outage")}
        </button>
      </header>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="incident-counts">
        {[
          { k: tr("Ouverts", "Open"), v: counts.open ?? 0, cls: (counts.open ?? 0) > 0 ? "text-red-700" : "text-emerald-700" },
          { k: tr("Pris en compte", "Acknowledged"), v: counts.acknowledged ?? 0, cls: "text-amber-700" },
          { k: tr("Résolus", "Resolved"), v: counts.resolved ?? 0, cls: "text-zinc-900" },
          { k: tr("Documentés", "Documented"), v: `${counts.documented ?? 0}/${counts.total ?? 0}`, cls: "text-zinc-900" },
        ].map((c) => (
          <div key={c.k} className="rounded-xl border border-zinc-200 bg-white p-4">
            <div className="text-xs uppercase tracking-wide text-zinc-500">{c.k}</div>
            <div className={`text-2xl font-bold mt-1 ${c.cls}`}>{c.v}</div>
          </div>
        ))}
      </div>

      {creating && (
        <section className="rounded-xl border border-[#002FA7]/30 bg-[#002FA7]/5 p-5 space-y-3" data-testid="incident-create-form">
          <h2 className="text-base font-semibold text-zinc-900">{tr("Nouvelle entrée", "New entry")}</h2>
          <input
            value={draft.title}
            onChange={(e) => setDraft({ ...draft, title: e.target.value })}
            placeholder={tr("Titre — ex. « Les vidéos ne se chargeaient plus chez Jean-Sébastien »", "Title — e.g. “Videos stopped loading for Jean-Sébastien”")}
            className="w-full text-sm rounded-lg border border-zinc-300 px-3 py-2"
            data-testid="incident-draft-title"
          />
          <textarea
            value={draft.message}
            onChange={(e) => setDraft({ ...draft, message: e.target.value })}
            rows={2}
            placeholder={tr("Symptôme observé", "Symptom observed")}
            className="w-full text-sm rounded-lg border border-zinc-300 px-3 py-2"
            data-testid="incident-draft-message"
          />
          <textarea
            value={draft.root_cause}
            onChange={(e) => setDraft({ ...draft, root_cause: e.target.value })}
            rows={2}
            placeholder={tr("Cause réelle", "Real cause")}
            className="w-full text-sm rounded-lg border border-zinc-300 px-3 py-2"
            data-testid="incident-draft-root-cause"
          />
          <textarea
            value={draft.fix}
            onChange={(e) => setDraft({ ...draft, fix: e.target.value })}
            rows={2}
            placeholder={tr("Correctif appliqué", "Fix applied")}
            className="w-full text-sm rounded-lg border border-zinc-300 px-3 py-2"
            data-testid="incident-draft-fix"
          />
          <div className="flex items-center gap-3">
            <select
              value={draft.severity}
              onChange={(e) => setDraft({ ...draft, severity: e.target.value })}
              className="text-sm rounded-lg border border-zinc-300 px-3 py-2"
              data-testid="incident-draft-severity"
            >
              <option value="critical">Critique</option>
              <option value="warning">Avertissement</option>
              <option value="info">Information</option>
            </select>
            <button
              onClick={createIncident}
              className="px-4 py-2 rounded-lg bg-[#002FA7] hover:bg-[#002280] text-white text-sm font-semibold"
              data-testid="incident-create-submit"
            >
              {tr("Ajouter au journal", "Add to the journal")}
            </button>
          </div>
        </section>
      )}

      <section className="rounded-xl border border-zinc-200 bg-white p-4" data-testid="incident-search">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[260px]">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={tr("Décris le symptôme — « la page se recharge toute seule »", "Describe the symptom — “the page reloads on its own”")}
              className="w-full text-sm rounded-lg border border-zinc-300 pl-9 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#002FA7]/30"
              data-testid="incident-search-input"
            />
          </div>
          <button
            onClick={() => setSemantic((v) => !v)}
            className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
              semantic
                ? "bg-[#002FA7] text-white border-[#002FA7]"
                : "bg-white text-zinc-700 border-zinc-300 hover:bg-zinc-50"
            }`}
            title={tr("Retrouve les pannes qui ressemblent au symptôme, même sans mot commun", "Finds outages that resemble the symptom, even with no word in common")}
            data-testid="incident-semantic-toggle"
          >
            <Brain size={16} />
            {tr("Recherche par sens", "Search by meaning")}
          </button>
          {query.trim() && (
            <button
              onClick={() => setQuery("")}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-zinc-300 text-sm text-zinc-700 hover:bg-zinc-50"
              data-testid="incident-search-clear"
            >
              <X size={14} />
              Effacer
            </button>
          )}
        </div>
        {query.trim() && (
          <p className="text-xs text-zinc-500 mt-2" data-testid="incident-search-status">
            {searching
              ? tr("Recherche…", "Searching…")
              : `${families.length} ${tr("famille(s) de panne", "outage family/families")} · ${incidents.length} ${tr("entrée(s)", "entry/entries")} · ${
                  data?.search_mode === "semantique"
                    ? tr("par similarité de sens", "by meaning similarity")
                    : tr("par mot-clé (insensible aux accents)", "by keyword (accent-insensitive)")
                }`}
            {semantic && data?.search_mode === "mot-cle" && (
              <span className="text-amber-700">
                {" "}{tr("— service d'embeddings indisponible, repli sur le mot-clé", "— embeddings service unavailable, falling back to keywords")}
              </span>
            )}
          </p>
        )}
      </section>

      <div className="flex items-center gap-2 flex-wrap" data-testid="incident-filters">
        {FILTERS.map((f) => (
          <button
            key={f.key || "all"}
            onClick={() => setFilter(f.key)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
              filter === f.key
                ? "bg-[#002FA7] text-white border-[#002FA7]"
                : "bg-white text-zinc-700 border-zinc-300 hover:bg-zinc-50"
            }`}
            data-testid={`incident-filter-${f.key || "all"}`}
          >
            {lang === "en" ? f.label_en : f.label}
          </button>
        ))}
      </div>

      <div className="space-y-3" data-testid="incident-list">
        {families.map((fam) => {
          const inc = fam.head;
          const sev = SEVERITY[inc.severity] || SEVERITY.info;
          const st = STATUS[inc.status] || STATUS.open;
          const documented = !!(inc.root_cause || inc.fix);
          return (
            <article
              key={fam.key}
              className="rounded-xl border border-zinc-200 bg-white p-5"
              data-testid={`incident-card-${inc.id}`}
            >
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${sev.cls}`}>
                      {sev.label}
                    </span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${st.cls}`}>
                      {st.label}
                    </span>
                    {inc.source === "historique" && (
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-zinc-100 text-zinc-600 border border-zinc-200">
                        historique
                      </span>
                    )}
                    {inc.source === "manuel" && (
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-zinc-100 text-zinc-600 border border-zinc-200">
                        {tr("saisi à la main", "entered by hand")}
                      </span>
                    )}
                    {fam.occurrences > 1 && (
                      <span className="text-xs text-zinc-500">
                        {fam.occurrences} occurrences
                      </span>
                    )}
                    {fam.returns > 0 && (
                      <span
                        className="px-2 py-0.5 rounded-full text-xs font-semibold bg-zinc-900 text-white"
                        title={tr("Cette panne est revenue après avoir été résolue", "This outage came back after being resolved")}
                        data-testid={`incident-returns-${inc.id}`}
                      >
                        {fam.returns} {tr(fam.returns > 1 ? "retours" : "retour", fam.returns > 1 ? "returns" : "return")} · {tr("dernier le", "last on")}{" "}
                        {fmt(fam.lastSeen)}
                      </span>
                    )}
                    {inc.similarity != null && (
                      <span
                        className="px-2 py-0.5 rounded-full text-xs font-medium bg-[#002FA7]/10 text-[#002FA7] border border-[#002FA7]/20"
                        title={tr("Proximité de sens avec ta recherche", "Meaning closeness to your search")}
                        data-testid={`incident-similarity-${inc.id}`}
                      >
                        {Math.round(inc.similarity * 100)} % de proximité
                      </span>
                    )}
                  </div>
                  <h3 className="text-base font-semibold text-zinc-900 mt-2">
                    <Highlighted text={inc.title} terms={data?.highlight} />
                  </h3>
                  <p className="text-sm text-zinc-600 mt-1">
                    <Highlighted text={inc.message} terms={data?.highlight} />
                  </p>
                  <p className="text-xs text-zinc-500 mt-1">
                    Détecté le {fmt(inc.detected_at)}
                    {inc.resolved_at ? ` · résolu le ${fmt(inc.resolved_at)}` : ""}
                  </p>
                </div>
                <button
                  onClick={() => setEditing(editing === inc.id ? null : inc.id)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-zinc-300 text-sm text-zinc-700 hover:bg-zinc-50 shrink-0"
                  data-testid={`incident-edit-btn-${inc.id}`}
                >
                  <PencilSimple size={14} />
                  {documented ? tr("Modifier", "Edit") : tr("Documenter", "Document it")}
                </button>
              </div>

              {editing === inc.id ? (
                <IncidentEditor
                  incident={inc}
                  onSave={(patch) => saveIncident(inc.id, patch)}
                  onCancel={() => setEditing(null)}
                />
              ) : (
                documented && (
                  <div className="mt-3 grid md:grid-cols-2 gap-3 border-t border-zinc-100 pt-3">
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-wide text-zinc-500">
                        {tr("Cause réelle", "Real cause")}
                      </div>
                      <p className="text-sm text-zinc-800 mt-1 whitespace-pre-wrap">
                        <Highlighted text={inc.root_cause || "—"} terms={data?.highlight} />
                      </p>
                    </div>
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-wide text-zinc-500">
                        Correctif
                      </div>
                      <p className="text-sm text-zinc-800 mt-1 whitespace-pre-wrap">
                        <Highlighted text={inc.fix || "—"} terms={data?.highlight} />
                      </p>
                    </div>
                  </div>
                )
              )}
              {!documented && editing !== inc.id && (
                <p className="mt-3 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  {tr(
                    "Cause réelle et correctif pas encore renseignés. Deux phrases maintenant valent mieux qu'une enquête dans six mois.",
                    "Real cause and fix not filled in yet. Two sentences now beat an investigation in six months.",
                  )}
                </p>
              )}

              {fam.others.length > 0 && (
                <div className="mt-3 border-t border-zinc-100 pt-2">
                  <button
                    onClick={() => setOpenFamily(openFamily === fam.key ? null : fam.key)}
                    className="text-xs font-semibold text-[#002FA7] hover:underline"
                    data-testid={`incident-family-toggle-${inc.id}`}
                  >
                    {openFamily === fam.key ? "Masquer" : "Voir"} les {fam.others.length}{" "}
                    autre(s) entrée(s) de cette famille
                  </button>
                  {openFamily === fam.key && (
                    <ul
                      className="mt-2 space-y-1"
                      data-testid={`incident-family-entries-${inc.id}`}
                    >
                      {fam.others.map((e) => (
                        <li
                          key={e.id}
                          className="flex items-baseline justify-between gap-3 text-xs text-zinc-600"
                        >
                          <span className="truncate">{e.title}</span>
                          <span className="shrink-0">
                            {STATUS[e.status]?.label || e.status} · {fmt(e.detected_at)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </article>
          );
        })}
        {incidents.length === 0 && (
          <p className="text-center text-zinc-500 py-10" data-testid="incident-empty">
            {query.trim()
              ? `Aucune panne ne correspond à « ${query.trim()} »${semantic ? "" : " — essaie la recherche par sens"}.`
              : "Aucun incident pour ce filtre."}
          </p>
        )}
      </div>
    </div>
  );
}
